import React from 'react';
import {
  Autocomplete,
  Box,
  Card,
  Button,
  TextField,
  MenuItem,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Client from '../Client';
import BackofficeReportsApi from './backofficeReportsApi';
import { format } from 'date-fns';

function thousandsSeparator(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

let d = new Date();
let today = format(d, 'yyyy-MM-dd');

const objectToCsv = function (data) {
  const csvRows = [];

  if (!data || !data.length) {
    return "No data";
  }

  const headers = Object.keys(data[0]);
  csvRows.push(headers.join(','));

  for (const row of data) {
    const values = headers.map(header => {
      const val = row[header];
      return `"${val}"`;
    });

    csvRows.push(values.join(','));
  }

  return csvRows.join('\n');
};

class ProdDateChartGroup extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      client: new Client("none"),
      saleslocation_list: { result: [] },
      page: 0,
      rowsPerPage: -1,
      order: 'asc',
      orderBy: null,

      product: 0,
      delivery: 2,
      dateTo: today,

      productName: { iref: 0, name: "All Products" },
      location: 0,
      locationName: { iref: 0, name: "All Locations" },

      products: [{ iref: 0, name: "All Products" }],
      locations: [{ iref: 0, name: "All Locations" }],

      deliverys: [
        { iref: 2, name: "All" },
        { iref: 0, name: "Restaurant" },
        { iref: 1, name: "Delivery" },
        { iref: 3, name: "App Clients" }
      ],

      filterData: {}
    };

    this.getData = this.getData.bind(this);
    this.loadSelectableItems = this.loadSelectableItems.bind(this);
    this.loadLocations = this.loadLocations.bind(this);
    this.doSync = this.doSync.bind(this);
    this.handleChangePage = this.handleChangePage.bind(this);
    this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this);
    this.selectDateTo = this.selectDateTo.bind(this);
    this.selectLocation = this.selectLocation.bind(this);
    this.selectProduct = this.selectProduct.bind(this);
    this.selectDelivery = this.selectDelivery.bind(this);
  }

  componentDidMount() {
    this.getData();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.salesType !== this.props.salesType) {
      const defaultName = this.isComboSales() ? "All Combos" : "All Products";

      this.setState(
        {
          product: 0,
          productName: { iref: 0, name: defaultName },
          products: [{ iref: 0, name: defaultName }],
          page: 0
        },
        function () {
          this.getData();
        }
      );
    }
  }

  getSalesType() {
    return this.props.salesType === "combos" ? "combos" : "products";
  }

  isComboSales() {
    return this.getSalesType() === "combos";
  }

  loadSelectableItems() {
    const isComboSales = this.isComboSales();
    const defaultName = isComboSales ? "All Combos" : "All Products";

    const productData = {
      salesType: isComboSales ? "combos" : "products"
    };

    const response = BackofficeReportsApi.getProductsCatalogSummary(
      this.state.client,
      productData
    );

    if (response && Array.isArray(response.products)) {
      this.setState({
        products: response.products.length
          ? response.products
          : [{ iref: 0, name: defaultName }]
      });
    } else {
      console.error("Products/combos list unavailable", {
        payload: productData,
        response: response
      });

      this.setState({
        products: [{ iref: 0, name: defaultName }]
      });
    }
  }

  loadLocations() {
    const response = BackofficeReportsApi.getLocations(this.state.client);

    if (response && Array.isArray(response.locations)) {
      this.setState({
        locations: response.locations.length
          ? response.locations
          : [{ iref: 0, name: "All Locations" }]
      });
    } else if (Array.isArray(response)) {
      this.setState({
        locations: response.length
          ? response
          : [{ iref: 0, name: "All Locations" }]
      });
    } else {
      console.error("Locations list unavailable", {
        payload: {},
        response: response
      });

      this.setState({
        locations: [{ iref: 0, name: "All Locations" }]
      });
    }
  }

  getData() {
    const queryData = {
      product: this.state.product,
      location: this.state.location,
      dateTo: this.state.dateTo,
      delivery: this.state.delivery,
      salesType: this.getSalesType()
    };

    const data = BackofficeReportsApi.getSalesProductDaily(this.state.client, queryData);

    if (data) {
      this.setState({ saleslocation_list: data });
    } else {
      this.setState({ saleslocation_list: { result: [] } });
    }

    this.setState({ filterData: queryData });

    this.loadSelectableItems();
    this.loadLocations();
  }

  doSync() {
    this.getData();
    this.setState({ page: 0 });
  }

  selectDateTo = (event) => {
    this.setState(
      {
        dateTo: event.target.value,
        page: 0
      },
      function () {
        this.doSync();
      }
    );
  }

  selectLocation = (event) => {
    const selectedValue = event.target.value || 0;

    const selectedObj = this.state.locations.find(
      (o) => String(o.iref) === String(selectedValue)
    );

    this.setState(
      {
        location: selectedValue,
        locationName: selectedObj || { iref: 0, name: "All Locations" },
        page: 0
      },
      function () {
        this.doSync();
      }
    );
  }

  selectProduct = (event) => {
    const selectedValue = event.target.value || 0;

    const selectedObj = this.state.products.find(
      (o) => String(o.iref) === String(selectedValue)
    );

    this.setState(
      {
        product: selectedValue,
        productName: selectedObj || {
          iref: 0,
          name: this.isComboSales() ? "All Combos" : "All Products"
        },
        page: 0
      },
      function () {
        this.doSync();
      }
    );
  }

  selectDelivery = (event) => {
    this.setState(
      {
        delivery: event.target.value,
        page: 0
      },
      function () {
        this.doSync();
      }
    );
  }

  handleChangePage = (event, newPage) => {
    this.setState({ page: newPage });
  }

  handleChangeRowsPerPage = (event) => {
    this.setState({
      rowsPerPage: +event.target.value,
      page: 0
    });
  }

  handleRequestSort = (property) => {
    const isAscending = this.state.orderBy === property && this.state.order === 'asc';
    this.setState({ order: isAscending ? 'desc' : 'asc', orderBy: property, page: 0 });
  }

  onDownload = () => {
    const rows = this.state.saleslocation_list?.result || [];

    const selectedProductName = this.state.productName?.name || (
      this.isComboSales() ? "All Combos" : "All Products"
    );

    const selectedLocationName = this.state.locationName?.name || "All Locations";

    const reportLabel = this.isComboSales() ? "Combo Sales" : "Product Sales";
    const itemLabel = this.isComboSales() ? "Combo" : "Product";
    const filePrefix = this.isComboSales() ? "comboSalesByDate" : "productSalesByDate";

    const link = document.createElement("a");
    link.download = filePrefix + "_" + selectedLocationName + "_" + selectedProductName + "_" + this.state.dateTo + ".csv";

    const csvData = objectToCsv(rows);
    const csvHeader =
      reportLabel +
      "\nLocation: " + selectedLocationName +
      "\n" + itemLabel + ": " + selectedProductName +
      "\nMonth to Date: " + this.state.dateTo +
      "\nDelivery: " + this.state.delivery;

    const csv = "\uFEFF" + csvHeader + "\n" + csvData;

    link.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    link.click();
  };

  filterSx(widthConfig) {
    return {
      width: widthConfig,
      minWidth: 0,
      '& .MuiInputBase-root': {
        height: 36,
        borderRadius: '12px',
        fontSize: {
          xs: '0.82rem',
          sm: '0.88rem',
        },
        bgcolor: '#fff',
      },
      '& .MuiInputBase-input': {
        fontSize: {
          xs: '16px',
          sm: '0.88rem',
        },
        py: 0.35,
      },
      '& .MuiInputLabel-root': {
        fontSize: {
          xs: '0.76rem',
          sm: '0.82rem',
        },
      },
    };
  }

  render() {
    const isComboSales = this.isComboSales();
    const allProductLabel = isComboSales ? "All Combos" : "All Products";

    let products = [{ iref: 0, name: allProductLabel }];

    if (Array.isArray(this.state.products)) {
      this.state.products.forEach((fData) => {
        if (
          fData &&
          fData.iref !== undefined &&
          String(fData.iref) !== "0"
        ) {
          products.push({
            iref: fData.iref,
            name: fData.name
          });
        }
      });
    }

    let locations = [{ iref: 0, name: "All Locations" }];

    if (Array.isArray(this.state.locations)) {
      this.state.locations.forEach((fData) => {
        if (
          fData &&
          fData.iref !== undefined &&
          String(fData.iref) !== "0"
        ) {
          locations.push({
            iref: fData.iref,
            name: fData.name
          });
        }
      });
    }

    let deliverys = [
      { iref: 2, name: "All" },
      { iref: 0, name: "Restaurant" },
      { iref: 1, name: "Delivery" },
      { iref: 3, name: "App Clients" }
    ];

    let productSalesData = [];
    let productSalesList = [];

    if (this.state.saleslocation_list && this.state.saleslocation_list.result) {
      let counter = 0;

      Object.keys(this.state.saleslocation_list.result).forEach(key => {
        let fData = this.state.saleslocation_list.result[key];

        let myData = {
          x: fData.date,
          y: parseInt(fData.value || 0)
        };

        let p = {
          id: counter + 1,
          date: fData.date,
          quantity: thousandsSeparator(fData.quantity || 0),
          value: thousandsSeparator(parseInt(fData.value || 0))
        };

        if (counter < 10) {
          productSalesData.push(myData);
        }

        counter += 1;
        productSalesList.push(p);
      });
    }

    const { order, orderBy } = this.state;
    const sortedProductSalesList = orderBy
      ? [...productSalesList].sort((a, b) => {
          const normalize = (value) => {
            const numericValue = Number(String(value ?? '').replace(/,/g, ''));
            return String(value ?? '').trim() !== '' && Number.isFinite(numericValue)
              ? numericValue
              : String(value ?? '').toLocaleLowerCase();
          };
          const aValue = normalize(a[orderBy]);
          const bValue = normalize(b[orderBy]);
          if (aValue < bValue) return order === 'asc' ? -1 : 1;
          if (aValue > bValue) return order === 'asc' ? 1 : -1;
          return 0;
        })
      : productSalesList;

    const selectedProductOption = products.find(
      (p) => String(p.iref) === String(this.state.product)
    ) || products[0];

    const selectedLocationOption = locations.find(
      (p) => String(p.iref) === String(this.state.location)
    ) || locations[0];

    const tableContainerSx = {
      flex: 1,
      minHeight: 0,
      overflow: 'auto',
      borderTop: '1px solid rgba(15, 23, 42, 0.06)',
      '&::-webkit-scrollbar': {
        height: 8,
        width: 8,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(15, 23, 42, 0.18)',
        borderRadius: 999,
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: 'rgba(15, 23, 42, 0.04)',
      },
    };

    const tableSx = {
      minWidth: {
        xs: '100%',
        sm: 520,
        md: '100%',
      },
      width: '100%',
      tableLayout: 'fixed',
      '& .MuiTableCell-root': {
        borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
        fontSize: {
          xs: '0.72rem',
          sm: '0.84rem',
          md: '0.92rem',
        },
      },
      '& .MuiTableRow-hover:hover': {
        backgroundColor: 'rgba(26, 35, 126, 0.035)',
      },
    };

    const headerCellSx = {
      py: {
        xs: 0.45,
        sm: 0.85,
      },
      px: {
        xs: 0.35,
        sm: 1,
      },
      bgcolor: '#f8fafc',
      color: '#1a237e',
      fontWeight: 650,
      lineHeight: 1.1,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    };

    const bodyCellSx = {
      py: {
        xs: 0.45,
        sm: 0.8,
      },
      px: {
        xs: 0.35,
        sm: 1,
      },
      whiteSpace: 'nowrap',
      color: '#111827',
      fontWeight: 450,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontVariantNumeric: 'tabular-nums',
    };

    const nameCellSx = {
      ...bodyCellSx,
      position: 'sticky',
      left: 0,
      zIndex: 2,
      bgcolor: '#fff',
      fontWeight: 550,
      fontVariantNumeric: 'normal',
    };

    const nameHeaderCellSx = {
      ...headerCellSx,
      position: 'sticky',
      left: 0,
      zIndex: 4,
    };

    const paginationSx = {
      flexShrink: 0,
      borderTop: '1px solid rgba(15, 23, 42, 0.06)',
      bgcolor: '#fff',
      minHeight: {
        xs: 44,
        sm: 52,
      },
      '& .MuiTablePagination-toolbar': {
        minHeight: {
          xs: 44,
          sm: 52,
        },
        px: {
          xs: 0.5,
          sm: 1,
        },
        flexWrap: {
          xs: 'wrap',
          sm: 'nowrap',
        },
        gap: {
          xs: 0.25,
          sm: 0.5,
        },
      },
      '& .MuiTablePagination-spacer': {
        display: {
          xs: 'none',
          sm: 'block',
        },
      },
      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
        fontSize: {
          xs: '0.88rem',
          sm: '0.92rem',
          md: '1rem',
        },
        m: 0,
      },
      '& .MuiTablePagination-actions': {
        ml: {
          xs: 0,
          sm: 1,
        },
      },
      '& .MuiTablePagination-actions button': {
        p: {
          xs: 0.25,
          sm: 1,
        },
      },
    };

    return (
      <Card
        sx={{
          height: '100%',
          maxHeight: '100%',
          minHeight: 0,
          borderRadius: 0,
          boxShadow: 'none',
          border: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: '#fff',
        }}
      >
        {/* Filters */}
        <Box
          sx={{
            px: {
              xs: 0.75,
              sm: 1,
            },
            py: {
              xs: 0.65,
              sm: 0.85,
            },
            flexShrink: 0,
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
            bgcolor: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: {
                xs: 'stretch',
                sm: 'center',
              },
              justifyContent: 'space-between',
              gap: {
                xs: 0.75,
                sm: 1,
              },
              flexDirection: {
                xs: 'column',
                sm: 'row',
              },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: '#1a237e',
                  fontWeight: 800,
                  fontSize: {
                    xs: '0.9rem',
                    sm: '1rem',
                  },
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Sales by Date
              </Typography>

              <Typography
                sx={{
                  mt: 0.15,
                  color: '#64748b',
                  fontWeight: 600,
                  fontSize: {
                    xs: '0.72rem',
                    sm: '0.78rem',
                  },
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Month-to-date quantity and value for selected {isComboSales ? 'combo' : 'product'}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: {
                  xs: 'space-between',
                  sm: 'flex-end',
                },
                gap: 0.75,
                flexWrap: 'wrap',
              }}
            >
              <TextField
                size="small"
                id="date-to"
                label="Month to"
                type="date"
                value={this.state.dateTo}
                sx={this.filterSx({
                  xs: 132,
                  sm: 130,
                })}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: "2023-01-01",
                  style: {
                    paddingRight: 5,
                    paddingLeft: 5
                  }
                }}
                onChange={this.selectDateTo}
              />

              <Autocomplete
                id="location-select"
                options={locations}
                getOptionLabel={(option) => option.name || ""}
                value={selectedLocationOption}
                isOptionEqualToValue={(option, value) => String(option?.iref) === String(value?.iref)}
                onChange={(event, newValue) =>
                  this.selectLocation({ target: { value: newValue?.iref || 0 } })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Select location"
                    sx={this.filterSx("100%")}
                  />
                )}
                sx={{
                  flex: {
                    xs: '1 1 calc(100% - 180px)',
                    sm: '0 1 180px',
                    md: '0 1 210px',
                    lg: '0 0 220px',
                  },
                  minWidth: {
                    xs: 150,
                    sm: 180,
                  },
                  maxWidth: {
                    xs: '100%',
                    md: 230,
                  },
                  '& .MuiAutocomplete-inputRoot': {
                    height: 36,
                    borderRadius: '12px',
                    bgcolor: '#fff',
                    py: 0,
                  },
                  '& .MuiAutocomplete-input': {
                    fontSize: {
                      xs: '16px',
                      sm: '0.88rem',
                    },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiAutocomplete-option': {
                    fontSize: '0.78rem',
                    minHeight: 28,
                    py: 0.25,
                  },
                }}
              />

              <Autocomplete
                id="product-select-date"
                options={products}
                getOptionLabel={(option) => option.name || ""}
                value={selectedProductOption}
                isOptionEqualToValue={(option, value) => String(option?.iref) === String(value?.iref)}
                onChange={(event, newValue) =>
                  this.selectProduct({ target: { value: newValue?.iref || 0 } })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder={isComboSales ? "Select combo" : "Select product"}
                    sx={this.filterSx("100%")}
                  />
                )}
                sx={{
                  flex: {
                    xs: '1 1 100%',
                    sm: '0 1 220px',
                    md: '0 1 240px',
                    lg: '0 0 260px',
                  },
                  minWidth: {
                    xs: '100%',
                    sm: 220,
                  },
                  maxWidth: {
                    xs: '100%',
                    md: 260,
                  },
                  '& .MuiAutocomplete-inputRoot': {
                    height: 36,
                    borderRadius: '12px',
                    bgcolor: '#fff',
                    py: 0,
                  },
                  '& .MuiAutocomplete-input': {
                    fontSize: {
                      xs: '16px',
                      sm: '0.88rem',
                    },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiAutocomplete-option': {
                    fontSize: '0.78rem',
                    minHeight: 28,
                    py: 0.25,
                  },
                }}
              />

              <TextField
                id="standard-select-delivery-date"
                select
                value={this.state.delivery}
                size="small"
                sx={this.filterSx({
                  xs: 108,
                  sm: 112,
                })}
                onChange={this.selectDelivery}
              >
                {deliverys.map((option) => (
                  <MenuItem key={option.iref} value={option.iref}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>

              <Tooltip title="Download CSV">
                <Button
                  size="small"
                  onClick={this.onDownload}
                  variant="contained"
                  sx={{
                    minWidth: 36,
                    width: 36,
                    height: 36,
                    p: 0,
                    flexShrink: 0,
                    borderRadius: '12px',
                    color: '#1a237e',
                    bgcolor: 'rgba(26, 35, 126, 0.08)',
                    boxShadow: 'none',
                    border: '1px solid rgba(26, 35, 126, 0.12)',
                    '&:hover': {
                      bgcolor: 'rgba(26, 35, 126, 0.12)',
                      boxShadow: 'none',
                    },
                  }}
                >
                  <FileDownloadIcon fontSize="small" />
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Table + Pagination */}
        <TableContainer sx={tableContainerSx}>
          <Table stickyHeader aria-label="product date sales table" size="small" sx={tableSx}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    ...nameHeaderCellSx,
                    width: {
                      xs: '54%',
                      sm: '56%',
                      md: '56%',
                    },
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'date'}
                    direction={orderBy === 'date' ? order : 'asc'}
                    onClick={() => this.handleRequestSort('date')}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>

                <TableCell
                  align="right"
                  sx={{
                    ...headerCellSx,
                    width: {
                      xs: '23%',
                      sm: '22%',
                      md: '22%',
                    },
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'quantity'}
                    direction={orderBy === 'quantity' ? order : 'asc'}
                    onClick={() => this.handleRequestSort('quantity')}
                  >
                    Quantity
                  </TableSortLabel>
                </TableCell>

                <TableCell
                  align="right"
                  sx={{
                    ...headerCellSx,
                    width: {
                      xs: '23%',
                      sm: '22%',
                      md: '22%',
                    },
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'value'}
                    direction={orderBy === 'value' ? order : 'asc'}
                    onClick={() => this.handleRequestSort('value')}
                  >
                    Value
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {(this.state.rowsPerPage > 0
                ? sortedProductSalesList.slice(
                  this.state.page * this.state.rowsPerPage,
                  this.state.page * this.state.rowsPerPage + this.state.rowsPerPage
                )
                : sortedProductSalesList
              ).map((product) => {
                return (
                  <TableRow hover key={product.id} tabIndex={-1}>
                    <TableCell
                      sx={{
                        ...nameCellSx,
                        width: {
                          xs: '54%',
                          sm: '56%',
                          md: '56%',
                        },
                      }}
                    >
                      {product.date}
                    </TableCell>

                    <TableCell align="right" sx={bodyCellSx}>
                      {product.quantity}
                    </TableCell>

                    <TableCell align="right" sx={bodyCellSx}>
                      {product.value}
                    </TableCell>
                  </TableRow>
                );
              })}

              {productSalesList.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Box
                      sx={{
                        py: 4,
                        color: 'text.secondary',
                        fontSize: {
                          xs: '0.86rem',
                          sm: '0.92rem',
                        },
                      }}
                    >
                      No data
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, { value: -1, label: 'All' }]}
          labelRowsPerPage="Rows:"
          component="div"
          count={productSalesList.length}
          rowsPerPage={this.state.rowsPerPage}
          page={this.state.page}
          onPageChange={this.handleChangePage}
          onRowsPerPageChange={this.handleChangeRowsPerPage}
          sx={paginationSx}
        />
      </Card>
    );
  }
}

export default ProdDateChartGroup;
